import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  EditEventByIdAction,
  LoadByIdEventAction,
  SubscribeToEventAction,
  TransferEventOwnershipAction,
  UnsubscribeToEventAction,
} from './detail-events.actions';
import { EventDetailOutputDto, EventsService, UnregisterToEventDto } from '@smatchy/sdk';
import { catchError, tap } from 'rxjs';
import { ShowToastErrorAction, ShowToastMessageAction } from '../../app';
import { LoadMyNextEventsAction } from './my-events.actions';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HOME_PATH } from '../../../../presentations/pages/logged/pages/home/home.routing.module';
import { toReportableError } from '../../../utils/reportable-error.util';
import { AnalyticsService } from '../../../services/analytics.service';

export interface DetailEventsStateModel {
  loading: boolean;
  subscribeLoading: boolean;
  ownershipTransferLoading: boolean;
  /*
   * Absent before anything is loaded, and absent again when the activity is
   * gone: the defaults set null and one action patches undefined. The model
   * declared it always present, which is the one thing it never is on the way
   * in or on the way out.
   */
  event?: EventDetailOutputDto | null;
}

const defaults: DetailEventsStateModel = {
  loading: false,
  subscribeLoading: false,
  ownershipTransferLoading: false,
  event: null,
};

@State<DetailEventsStateModel>({
  name: 'detailEvents',
  defaults,
})
@Injectable()
export class DetailEventsState {
  constructor(
    private readonly _eventsService: EventsService,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _analytics: AnalyticsService,
  ) {}

  @Action(LoadByIdEventAction)
  async loadByIdEventAction(
    { patchState, dispatch }: StateContext<DetailEventsStateModel>,
    { payload, redirectIfMissing }: LoadByIdEventAction,
  ) {
    // A dispatch with no id reached the API as /events/details/undefined, which
    // answers 400, and was then reported as a failure on the Explore screen the
    // catch below bounces to — the activity id was simply missing (a card
    // without one, a deep link or push whose parameter never arrived, a
    // conversation whose activity reference is empty). There is nothing to
    // load, so treat it exactly like a missing activity rather than asking the
    // server about `undefined`. `'undefined'` is caught too: a router segment
    // built from an undefined value arrives as that literal string.
    if (!payload || payload === 'undefined') {
      patchState({ loading: false, event: undefined });
      if (redirectIfMissing) {
        dispatch(
          new ShowToastErrorAction(this._translate.instant('ACTIVITY_DETAIL_PAGE.EVENT_NOT_FOUND'), undefined, 3000),
        );
        this._router.navigate([HOME_PATH, 'explore']);
      }
      return;
    }
    patchState({ loading: true });
    return this._eventsService
      .getEventById({
        id: payload,
      })
      .pipe(
        tap((v) => {
          if (v?.error && typeof v.error === 'object') {
            this.redirectToGroupDetail(v?.error['groupId'], dispatch);
            return;
          } else if (v.error === 'event_not_found') {
            // A deleted activity leaves its conversation behind, and the API now
            // answers that conversation correctly instead of 500ing. The chat
            // screen then asks for the activity just to read its end date, and
            // used to be thrown back to Explore by this branch: the server fix
            // was what triggered the eviction. Callers that only want a detail
            // say so, and simply get nothing.
            patchState({ loading: false, event: undefined });
            if (redirectIfMissing) {
              dispatch(
                new ShowToastErrorAction(
                  this._translate.instant('ACTIVITY_DETAIL_PAGE.EVENT_NOT_FOUND'),
                  undefined,
                  3000,
                ),
              );
              this._router.navigate([HOME_PATH, 'explore']);
            }
            return;
          }
          patchState({ loading: false, event: v?.value?.event });
        }),
        catchError((error) => {
          dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.LABELS.DEFAULT_ERROR'), undefined, 3000));
          this._router.navigate([HOME_PATH, 'explore']);
          // `new Error(error)` stringified Angular's HttpErrorResponse, so every
          // report read "Error: [object Object]" and the status, the URL and the
          // reason were destroyed at the moment they were about to be reported
          // (CAPACITOR-9). Note also that the navigation above happens first, so
          // Sentry attributes the failure to the Explore screen it lands on
          // rather than to the activity that failed to load; the message now
          // says which activity it was.
          throw toReportableError(error, `activity ${payload} could not be loaded`);
        }),
      );
  }
  private redirectToGroupDetail = (groupId: string, dispatch: StateContext<DetailEventsStateModel>['dispatch']) => {
    dispatch(
      new ShowToastErrorAction(
        this._translate.instant('ACTIVITY_DETAIL_PAGE.REQUIRES_GROUP_MEMBERSHIP'),
        undefined,
        3000,
      ),
    );
    sessionStorage.setItem('redirectedFromActivityDetail', 'true');
    sessionStorage.setItem('redirectedToGroupId', groupId);
    this._router.navigate(['home/group', groupId]);
  };

  @Action(EditEventByIdAction)
  async editEventByIdAction(
    { patchState, getState, dispatch }: StateContext<DetailEventsStateModel>,
    { payload }: EditEventByIdAction,
  ) {
    const state = getState();
    if (!state.event) {
      console.error('Cannot edit event because event is not loaded in state');
      return;
    }
    return this._eventsService
      .updateEvent({
        id: payload.id,
        updateEventDto: {
          ...payload.data,
          ownerId: state.event.owner.id,
        },
      })
      .pipe(
        tap((v) => {
          if (v.error) {
            console.error(v.error);
            dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.LABELS.DEFAULT_ERROR')));
            return;
          }
          patchState({ loading: false });
          dispatch(new LoadByIdEventAction(payload.id));
        }),
      );
  }

  @Action(UnsubscribeToEventAction)
  async unsubscribeToEventAction(
    { patchState, dispatch }: StateContext<DetailEventsStateModel>,
    { eventId, reason, nextOwnerId, reasonCustomText, refundDestination }: UnsubscribeToEventAction,
  ) {
    patchState({ subscribeLoading: true });
    // refundDestination is not (yet) in the generated DTO; it is threaded
    // through as an extra field the API reads. Cast so the excess property is
    // kept at runtime rather than dropped by the type.
    const unregisterToEventDto = {
      reason,
      eventId,
      ...(nextOwnerId ? { nextOwnerId } : {}),
      ...(reasonCustomText ? { reasonCustomText } : {}),
      ...(refundDestination ? { refundDestination } : {}),
    } as UnregisterToEventDto & { refundDestination?: 'wallet' | 'card' };
    return this._eventsService
      .leaveEvent({
        id: eventId,
        unregisterToEventDto,
      })
      .pipe(
        tap((v) => {
          if (v.error) {
            patchState({ subscribeLoading: false });
            const errorCode = typeof v.error === 'string' ? v.error : undefined;
            dispatch(
              new ShowToastErrorAction(
                this._translate.instant(this.getOwnershipTransferErrorKey(errorCode)),
                undefined,
                3000,
              ),
            );
            if (errorCode === 'ownership_transfer_conflict') {
              dispatch(new LoadByIdEventAction(eventId));
            }
            return;
          }
          dispatch(new LoadByIdEventAction(eventId));
          patchState({ subscribeLoading: false });
          // At the bottom, like every other toast of the activity screen and
          // like the error this same action shows when it fails: at the top it
          // landed over the screen's header and could not be read (recette
          // 2.2.11).
          dispatch(
            new ShowToastMessageAction(
              this._translate.instant('ACTIVITY_DETAIL_PAGE.REGISTRATION_CANCELLED_TOAST'),
              undefined,
              undefined,
              'bottom',
            ),
          );
          dispatch(new LoadMyNextEventsAction());
        }),
      );
  }

  @Action(TransferEventOwnershipAction)
  transferEventOwnershipAction(
    { patchState, dispatch }: StateContext<DetailEventsStateModel>,
    { eventId, nextOwnerId }: TransferEventOwnershipAction,
  ) {
    patchState({ ownershipTransferLoading: true });
    return this._eventsService
      .transferEventOwnership({
        id: eventId,
        transferOwnershipDto: { nextOwnerId },
      })
      .pipe(
        tap(({ error, value }) => {
          patchState({ ownershipTransferLoading: false });
          if (error || !value?.success) {
            const errorCode = typeof error === 'string' ? error : undefined;
            const errorKey = this.getOwnershipTransferErrorKey(errorCode);
            dispatch(new ShowToastErrorAction(this._translate.instant(errorKey), undefined, 3000));
            if (errorCode === 'ownership_transfer_conflict') {
              dispatch(new LoadByIdEventAction(eventId));
            }
            return;
          }

          dispatch(new ShowToastMessageAction(this._translate.instant('OWNERSHIP_TRANSFER.SUCCESS_EVENT')));
          dispatch(new LoadByIdEventAction(eventId));
          dispatch(new LoadMyNextEventsAction());
          this._router.navigate([HOME_PATH, 'detail-activity', eventId]);
        }),
        catchError((error) => {
          console.error(error);
          patchState({ ownershipTransferLoading: false });
          dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.LABELS.DEFAULT_ERROR'), undefined, 3000));
          throw error;
        }),
      );
  }

  private getOwnershipTransferErrorKey(errorCode?: string): string {
    const errorTranslations: Record<string, string> = {
      event_invalid_next_owner: 'OWNERSHIP_TRANSFER.INVALID_MEMBER',
      event_not_found: 'ACTIVITY_DETAIL_PAGE.EVENT_NOT_FOUND',
      forbidden: 'OWNERSHIP_TRANSFER.OWNER_ONLY',
      ownership_transfer_conflict: 'OWNERSHIP_TRANSFER.CONFLICT',
      event_paid_transfer_not_allowed: 'OWNERSHIP_TRANSFER.PAID_EVENT_FORBIDDEN',
    };
    // An answer with no code at all lands on the default message, same as one
    // with a code this map does not know.
    return (errorCode && errorTranslations[errorCode]) || 'GENERICS.LABELS.DEFAULT_ERROR';
  }

  @Action(SubscribeToEventAction)
  async subscribeToEventAction(
    { patchState, dispatch, getState }: StateContext<DetailEventsStateModel>,
    { eventId }: SubscribeToEventAction,
  ) {
    patchState({ subscribeLoading: true });
    // Captured before the join, because the success branch below dispatches a
    // reload and the state may already have moved on.
    const joined = getState().event;
    return this._eventsService
      .joinEvent({
        id: eventId,
      })
      .pipe(
        tap((v) => {
          if (v.error) {
            const message =
              v.error === 'event_expired'
                ? this._translate.instant('ACTIVITY_DETAIL_PAGE.EVENT_EXPIRED')
                : v.error === 'event_full'
                ? this._translate.instant('GROUP_INVITATION.ACTIVITY_FULL')
                : v.error === 'premium_required'
                ? // A brand partner event, open to Premium only (15 October 2026).
                  this._translate.instant('PREMIUM.REQUIRED')
                : this._translate.instant('GENERICS.ERROR');
            dispatch(new ShowToastErrorAction(message));
            console.error(v.error);
            patchState({ subscribeLoading: false });
            return;
          }
          // Logged here rather than in the component: this is where the server
          // has confirmed the join. The component only knows the call returned.
          this._analytics.logActivityJoin({
            activityId: eventId,
            sport: joined?.sportKeyId,
            city: joined?.location?.name,
            // `?? 0` on the left of the comparison, not around it: with `?.`
            // the value is `number | undefined`, and an undefined price is a
            // free activity here, which is what the analytics line means.
            isPaid: (joined?.costPerParticipant ?? 0) > 0,
            seatsLeft: joined?.nbPlacesLeft,
          });
          dispatch(new LoadByIdEventAction(eventId));
          patchState({ subscribeLoading: false });
          dispatch(new LoadMyNextEventsAction());
        }),
      );
  }
}
